import type { QuestionWithSlide } from '@mentimeter/editor-schema/api-types-overrides';
import type { Question } from '@mentimeter/legacy-series-types/question';
import type { Slide as InternalSlide } from '../../../compatibility-overrides/editor-types';
import { isProxyInstance } from '../../../utils/is-proxy-instance';
import { getCommonSlideCompatibilityHandler } from '../../common/get-common-slide-compatibility-handler';
import {
  editorSlidePropertyKeys,
  editorQuestionPropertyKeys,
} from './constants';
import { mapEditorSlidePropertyToQuestionProperty } from './map-editor-slide-property-to-question-property';
import { mapEditorQuestionPropertyToSlideProperty } from './map-editor-question-property-to-slide-property';

type Key =
  | keyof InternalSlide
  | keyof Question
  | '__isProxy'
  | '__target'
  | 'isMigrated';

const getEditorSlideCompatibilityHandler = (): ProxyHandler<
  InternalSlide | Question
> => {
  // Get the common compatibility handler
  const commonHandler = getCommonSlideCompatibilityHandler();

  return {
    get(target: InternalSlide | Question, key: Key, receiver: any) {
      const isTargetMigrated = Reflect.has(target, 'staticContent');
      // Handle editor specific slide properties
      if (editorSlidePropertyKeys.includes(key as keyof InternalSlide)) {
        const slide = target as InternalSlide;
        if (isTargetMigrated) {
          // data is already migrated, return the value directly
          // 'hasComments' -> 'hasComments'
          return slide[key as keyof typeof slide];
        }

        // data is not migrated map editor slide property to question property
        // 'hasComments' -> 'has_comments'
        return mapEditorSlidePropertyToQuestionProperty(
          target as Question,
          key as keyof InternalSlide,
        );
      }

      // Handle editor specific question properties
      if (editorQuestionPropertyKeys.includes(key as keyof Question)) {
        // Handle editor specific slide properties
        if (isTargetMigrated) {
          const slide = target as InternalSlide;
          // map has_comments -> hasComments
          return mapEditorQuestionPropertyToSlideProperty(
            slide,
            key as keyof Question,
          );
        }

        // data is not migrated yet, return the value directly
        // 'has_comments' -> 'has_comments'
        return (target as Question)[key as keyof Question];
      }

      // Fallback to the common compatibility handler
      return commonHandler.get
        ? commonHandler.get(target, key, receiver)
        : Reflect.get(target, key, receiver);
    },

    set(
      target: InternalSlide | Question,
      key: keyof InternalSlide | keyof Question,
      value: any,
      receiver: any,
    ) {
      return commonHandler.set
        ? commonHandler.set(target, key, value, receiver)
        : Reflect.set(target, key, value, receiver);
    },
  };
};

export function createEditorSlideCompatibilityLayer(
  _target: unknown,
): QuestionWithSlide {
  const target = _target as Question | InternalSlide;

  if (isProxyInstance(target)) {
    return target as QuestionWithSlide;
  }

  return new Proxy(
    target,
    getEditorSlideCompatibilityHandler(),
  ) as QuestionWithSlide;
}
